import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, ChevronDown, Loader2, Play } from 'lucide-react';
import { ContentContainer } from '@/components/shell/ContentContainer';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { CatalogReaderView } from '@/components/catalog/CatalogReaderView';
import { CombinedAudioPlayer } from '@/components/series/CombinedAudioPlayer';
import {
  fetchCatalogCombinedPlayback,
  fetchCatalogPlayback,
  fetchCatalogRead,
  fetchCatalogWorldDetail,
  triggerCatalogCombinedAudio,
  unlockCatalogFolge,
  unlockCatalogWorld,
} from '@/features/catalog/api';
import type {
  CatalogAgeCluster,
  CatalogCombinedPlayback,
  CatalogDetailFolge,
  CatalogGender,
  CatalogReadContent,
  CatalogWorldDetail,
} from '@/features/catalog/types';
import { listChildren } from '@/features/children/api';
import type { Child } from '@/lib/auth-types';
import { useAudioStore } from '@/stores/audioStore';
import { useUiStore } from '@/stores/uiStore';
import { useEntitlement, clearEntitlementCache } from '@/hooks/useEntitlement';
import { ApiError } from '@/lib/api-client';
import { STRINGS } from '@/strings/strings';

/**
 * MIRRORS backend ChildrenService.calculateAgeCluster(age, 'sb').
 * SB catalog has only age_3_4 and age_5_7 (no 0-2 content; 0-4 maps to 3-4).
 * If the backend rule ever changes, THIS MUST CHANGE WITH IT.
 * Tracked for unification (Option B) on the post-launch tech-debt list.
 */
function deriveSbCatalogCluster(age: number): CatalogAgeCluster {
  return age <= 4 ? 'age_3_4' : 'age_5_7';
}

/**
 * Catalog world-detail. Unlocked Folgen are playable (anhören → signed catalog
 * audio via the url-direct 'catalog' track kind) and readable (lesen → full
 * storyText + Wissen-für-Eltern box, reusing the AA/KP StoryBody + ParentInfoBox
 * presentational components). Locked Folgen show a lock.
 *
 * Child selector (Block 3): the visible age selector is DERIVED from the
 * account's children — 0 children → the age dropdown stays as today; 1 child →
 * no field (auto-selected invisibly); >1 children → a child dropdown (first
 * child auto-selected on load, user may switch). The selected child's age
 * drives the SB catalog cluster via deriveSbCatalogCluster() and its id flows
 * to the playback endpoint so the backend stitches the per-child "Hallo Name."
 * intro (behind CATALOG_INTRO_ENABLED). The male/female voice toggle stays
 * an independent parent preference and is never derived from child.gender.
 * Entitlement is enforced server-side (404 for locked).
 */
export function CatalogWorldRoute() {
  const S = STRINGS.catalog.detail;
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const loadCatalog = useAudioStore((s) => s.loadCatalog);
  const play = useAudioStore((s) => s.play);
  const close = useAudioStore((s) => s.close);

  const { entitlement } = useEntitlement();
  const [ageCluster, setAgeCluster] = useState<CatalogAgeCluster>('age_5_7'); // default ältere; ONLY used when the household has 0 children.
  const [gender, setGender] = useState<CatalogGender>('male'); // default männlich
  const [detail, setDetail] = useState<CatalogWorldDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [blockedCta, setBlockedCta] = useState<{ label: string; target: string } | null>(null);
  const [reader, setReader] = useState<CatalogReadContent | null>(null);
  // Local balance overrides the cached entitlement after an unlock (the unlock
  // response returns newTotalRemaining; clearEntitlementCache refreshes the rest).
  const [balance, setBalance] = useState<number | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const credits = balance ?? entitlement?.totalRemaining ?? 0;

  // Block 3 — child list owned by this route (single fetch, single source of
  // truth for count). null = loading; [] = fetched OR fetch failed (treated as
  // zero → falls back to the age dropdown, preserving today's UX exactly).
  const [children, setChildren] = useState<Child[] | null>(null);
  const [childId, setChildId] = useState<string | null>(null);
  const hasAutoSelectedRef = useRef(false);

  // Paket 3 — whole-world unlock + Gesamt-Hörbuch state.
  //
  // `unlockingAll`: in-flight for the /unlock-all POST — disables the button
  // to prevent double-clicks. Cleared on success/failure.
  // `combined`: last-known server response for the (world × cluster × gender)
  // combined-audio row. Null until the effect has fetched at least once (or
  // when the trigger endpoint would 403 because not all Folgen are owned).
  // `combining`: in-flight for the /combined-audio POST trigger. Cleared once
  // the server response confirms the new status. Distinct from
  // combined.status='pending' — the latter is server-observed, the former is
  // client-side "just clicked".
  const [unlockingAll, setUnlockingAll] = useState(false);
  const [combined, setCombined] = useState<CatalogCombinedPlayback | null>(null);
  const [combining, setCombining] = useState(false);
  const setCatalogWorldCombinedPlayerOpen = useUiStore(
    (s) => s.setCatalogWorldCombinedPlayerOpen,
  );

  useEffect(() => {
    let cancelled = false;
    void listChildren()
      .then((list) => {
        if (!cancelled) setChildren(list);
      })
      .catch(() => {
        // Fetch failure is not user-facing here — degrade to the zero-child
        // path (age dropdown, no childId, no intro). The user always gets
        // playable audio, never an error.
        if (!cancelled) setChildren([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-select the first child once — never overrides a subsequent manual
  // pick. Fires only when the fetch resolved with ≥1 child; a zero-child
  // household leaves childId=null so the fallback age dropdown drives cluster.
  useEffect(() => {
    if (!children || hasAutoSelectedRef.current) return;
    // Local narrow: under noUncheckedIndexedAccess, `children[0]` is
    // `Child | undefined` even after a length check. Zero-child households
    // early-return here (correct: no childId set → age dropdown fallback).
    const first = children[0];
    if (!first) return;
    setChildId(first.id);
    hasAutoSelectedRef.current = true;
  }, [children]);

  const selectedChild =
    children && childId ? (children.find((c) => c.id === childId) ?? null) : null;
  const effectiveCluster: CatalogAgeCluster = selectedChild
    ? deriveSbCatalogCluster(selectedChild.age)
    : ageCluster;

  // iOS-Safari pre-fetch cache (Option A). The signed catalog audio URL is
  // minted per (episodeNumber, effectiveCluster, gender, childId) and is NOT
  // on the /detail payload, so handleListen's await broke the iOS user-gesture
  // chain on a cold first tap. We prefetch the URL on pointer/touch intent
  // (just before the click) and seat+play synchronously on a cache-hit so no
  // await stands between the tap and play(). Keyed on the FULL quadruple so a
  // cluster/voice/child change after a prefetch can never replay a stale
  // variant — a non-matching key simply falls back to a fresh fetch. inFlight
  // de-dupes concurrent intents.
  const prefetchCache = useRef<
    Map<string, { url: string; downloadUrl: string | null; durationS: number | null }>
  >(new Map());
  const prefetchInFlight = useRef<Set<string>>(new Set());
  const variantKey = (episodeNumber: number): string =>
    `${episodeNumber}|${effectiveCluster}|${gender}|${childId ?? '-'}`;

  // Drop prefetched URLs when any variant dimension changes (cleanup runs on
  // effectiveCluster / gender / childId change) and on unmount. The quadruple-
  // key already guarantees correctness; clearing keeps the map bounded. A late
  // in-flight prefetch may repopulate its own key after a clear — harmless,
  // since that entry is still correct for its (now non-current) quadruple and
  // only ever consumed via an exact key match.
  useEffect(() => {
    const cache = prefetchCache.current;
    const inFlight = prefetchInFlight.current;
    return () => {
      cache.clear();
      inFlight.clear();
    };
  }, [effectiveCluster, gender, childId]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    // Wait for the child list to resolve before firing the detail fetch —
    // avoids one wasted request that would use the default age cluster before
    // the auto-select settles on the child-derived cluster.
    if (!slug || children === null) return;
    let active = true;
    // Resets live inside the async callback (not the synchronous effect body)
    // so the loading reset doesn't cascade-render (react-hooks/set-state-in-effect).
    void (async () => {
      setDetail(null);
      setLoadError(null);
      try {
        const data = await fetchCatalogWorldDetail(slug, effectiveCluster);
        if (active) setDetail(data);
      } catch {
        if (active) setLoadError(S.loadFailed);
      }
    })();
    return () => {
      active = false;
    };
  }, [slug, effectiveCluster, refreshKey, S.loadFailed, children]);

  // Fire-and-forget prefetch on pointer/touch intent. Safe to call repeatedly:
  // a hit/in-flight key short-circuits, and fetchCatalogPlayback is a pure
  // signing GET (no side effects), so a prefetch that's never played is inert.
  const prefetchListen = (f: CatalogDetailFolge): void => {
    if (!slug) return;
    const key = variantKey(f.episodeNumber);
    if (prefetchCache.current.has(key) || prefetchInFlight.current.has(key)) return;
    prefetchInFlight.current.add(key);
    void fetchCatalogPlayback(slug, f.episodeNumber, effectiveCluster, gender, childId ?? undefined)
      .then((pb) => {
        prefetchCache.current.set(key, {
          url: pb.url,
          downloadUrl: pb.downloadUrl,
          durationS: pb.audioDurationS,
        });
      })
      .catch(() => {
        // Swallow — handleListen falls back to the live await path on a miss.
      })
      .finally(() => {
        prefetchInFlight.current.delete(key);
      });
  };

  // Open the per-Folge reader page (the same page lesen opens). Called AFTER the
  // play trigger so the read fetch's await never stands between the tap and
  // play() — the iOS gesture token must survive (Möglichkeit 3 relocation: the
  // player now lives at the top of that page). Fire-and-forget.
  const openReaderPage = (f: CatalogDetailFolge): void => {
    if (!slug) return;
    void fetchCatalogRead(slug, f.episodeNumber, effectiveCluster)
      .then((content) => {
        setReader(content);
        window.scrollTo({ top: 0 });
      })
      .catch(() => setActionError(S.readFailed));
  };

  const handleListen = async (f: CatalogDetailFolge): Promise<void> => {
    if (!slug || !detail) return;
    setActionError(null);
    // Cache-hit: seat + play synchronously (no await before play()) so the iOS
    // Safari user-gesture token survives the tap. Mirrors the story player's
    // "URL seated ahead of tap" guarantee. Opening the reader page (an async
    // read fetch) happens AFTER the synchronous play trigger, never before.
    const cached = prefetchCache.current.get(variantKey(f.episodeNumber));
    if (cached) {
      loadCatalog({
        url: cached.url,
        downloadUrl: cached.downloadUrl,
        title: f.title ?? detail.title,
        durationS: cached.durationS,
      });
      void play();
      openReaderPage(f);
      return;
    }
    // Cache-miss: today's flow, unchanged.
    try {
      const pb = await fetchCatalogPlayback(
        slug,
        f.episodeNumber,
        effectiveCluster,
        gender,
        childId ?? undefined,
      );
      loadCatalog({
        url: pb.url,
        downloadUrl: pb.downloadUrl,
        title: f.title ?? detail.title,
        durationS: pb.audioDurationS,
      });
      await play();
      openReaderPage(f);
    } catch {
      // A real load failure (network drop / a missing audio variant 404'ing)
      // must not leave the previously-played Folge audible or its duration on
      // screen. Stop + clear ONLY when the seated track is a catalog one, so a
      // story / series-combined track playing elsewhere is never interrupted.
      if (useAudioStore.getState().kind === 'catalog') close();
      setActionError(S.playFailed);
    }
  };

  const handleRead = async (f: CatalogDetailFolge): Promise<void> => {
    if (!slug) return;
    setActionError(null);
    try {
      const content = await fetchCatalogRead(slug, f.episodeNumber, effectiveCluster);
      setReader(content);
      window.scrollTo({ top: 0 }); // reader is a full-page view — start at the top
    } catch {
      setActionError(S.readFailed);
    }
  };

  const handleUnlock = async (f: CatalogDetailFolge): Promise<void> => {
    if (!slug) return;
    setActionError(null);
    setBlockedCta(null);
    try {
      const res = await unlockCatalogFolge(slug, f.episodeNumber);
      setBalance(res.newTotalRemaining); // optimistic balance from the unlock
      clearEntitlementCache();
      setRefreshKey((k) => k + 1); // refetch /detail → Folge flips to anhören/lesen
    } catch (e) {
      if (e instanceof ApiError && e.httpStatus === 402) {
        // blocked_limit envelope: show its verbatim message + the Pakete-CTA.
        setActionError(e.envelope?.user_message ?? S.unlockFailed);
        if (e.envelope?.cta) setBlockedCta(e.envelope.cta);
      } else {
        setActionError(S.unlockFailed);
      }
    }
  };

  const handleUnlockAll = async (): Promise<void> => {
    if (!slug || unlockingAll) return;
    setActionError(null);
    setBlockedCta(null);
    setUnlockingAll(true);
    try {
      const res = await unlockCatalogWorld(slug, effectiveCluster);
      setBalance(res.newTotalRemaining);
      clearEntitlementCache();
      // Refetch /detail — Folgen the loop unlocked flip to anhören/lesen;
      // Folgen the loop skipped (skippedNoCredits > 0) stay locked with
      // their per-Folge "Freischalten · 1 Credit" CTA, letting the user
      // top up and retry per-Folge without re-running the whole batch.
      setRefreshKey((k) => k + 1);
    } catch (e) {
      // The endpoint returns 200 on partial success (skippedNoCredits > 0).
      // A thrown error is a true failure — network, 404 (unpublished world),
      // etc. Reuse the same error surface as per-Folge unlock (Paket 3 scope:
      // no new German string is introduced for this path).
      if (e instanceof ApiError && e.httpStatus === 402) {
        setActionError(e.envelope?.user_message ?? S.unlockFailed);
        if (e.envelope?.cta) setBlockedCta(e.envelope.cta);
      } else {
        setActionError(S.unlockFailed);
      }
    } finally {
      setUnlockingAll(false);
    }
  };

  const unlockedCount = detail?.folgen.filter((f) => f.unlocked).length ?? 0;
  const allUnlocked =
    detail !== null && detail.folgen.length > 0 && unlockedCount === detail.folgen.length;

  const handleTriggerCombined = async (): Promise<void> => {
    if (!slug || combining) return;
    setActionError(null);
    setCombining(true);
    try {
      const res = await triggerCatalogCombinedAudio(slug, effectiveCluster, gender);
      // Optimistically reflect the server-reported status so the button
      // flips to its muted / player state immediately; the polling effect
      // reconciles as the processor progresses.
      setCombined((prev) => ({
        worldId: res.worldId,
        worldSlug: res.worldSlug,
        ageCluster: res.ageCluster,
        gender: res.gender,
        status: res.status,
        url: prev?.status === 'ready' ? prev.url : null,
        downloadUrl: prev?.status === 'ready' ? prev.downloadUrl : null,
        durationMs: prev?.status === 'ready' ? prev.durationMs : null,
      }));
    } catch {
      // Failure surface reuses the per-Folge error line (no new string).
      setActionError(S.playFailed);
    } finally {
      setCombining(false);
    }
  };

  // Paket 3 — fetch combined-audio status when the world is fully unlocked
  // for the current (cluster × gender). Poll while status='pending' so the
  // player surfaces automatically once the processor writes 'ready'. Guard:
  // do not call the endpoint when the user does not own every Folge (the
  // backend would 403). Guard: refetch on cluster/gender/childId changes
  // because those change the target (world × cluster × gender) triple.
  useEffect(() => {
    if (!slug || !allUnlocked) {
      setCombined(null);
      return;
    }
    let active = true;
    const load = async (): Promise<void> => {
      try {
        const res = await fetchCatalogCombinedPlayback(slug, effectiveCluster, gender);
        if (active) setCombined(res);
      } catch {
        // Silent — the button surface degrades to the trigger state on any
        // fetch failure; a re-render on a subsequent tick retries.
      }
    };
    void load();
    const shouldPoll = combined?.status === 'pending';
    if (!shouldPoll) return () => {
      active = false;
    };
    const id = window.setInterval(() => void load(), 4000);
    return () => {
      active = false;
      window.clearInterval(id);
    };
  }, [slug, effectiveCluster, gender, allUnlocked, combined?.status]);

  // Paket 3 — mirror the catalogReaderOpen pattern: while the whole-world
  // CombinedAudioPlayer is mounted on this route, suppress the global
  // MiniPlayer to avoid a double control surface for the same track. The
  // effect fires only when the player is actually mounted (status='ready'
  // AND url present), NOT on every /catalog/:slug visit, so a user with
  // an unrelated story playing keeps their MiniPlayer controls until the
  // combined player itself surfaces.
  const combinedPlayerMounted = combined?.status === 'ready' && !!combined.url;
  useEffect(() => {
    if (!combinedPlayerMounted) return;
    setCatalogWorldCombinedPlayerOpen(true);
    return () => setCatalogWorldCombinedPlayerOpen(false);
  }, [combinedPlayerMounted, setCatalogWorldCombinedPlayerOpen]);

  // Reader (lesen) takes over the full route in the SB-Sachgeschichte
  // Story-Output chrome (mascot + Welt-Label + card), page-level scroll.
  if (reader) {
    return <CatalogReaderView content={reader} onClose={() => setReader(null)} />;
  }

  return (
    <ContentContainer maxWidth="default">
      <button
        type="button"
        onClick={() => navigate('/catalog')}
        aria-label={S.back}
        className="mb-4 -ml-1 shrink-0 rounded-md p-1 text-dream-navy hover:bg-dream-navy/5"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>

      {loadError && <p className="text-sm text-[color:var(--color-error)]">{loadError}</p>}

      {!detail && !loadError && (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-dream-navy/50" aria-hidden />
        </div>
      )}

      {detail && (
        <>
          <h1 className="mb-1 text-[22px] font-semibold text-dream-navy">{detail.title}</h1>
          <p className="text-sm text-dream-navy/70">
            {S.subline
              .replace('{unlocked}', String(unlockedCount))
              .replace('{total}', String(detail.folgen.length))}
          </p>
          <p className="mb-4 text-xs text-dream-navy/60">
            {S.creditsLine.replace('{credits}', String(credits))}
          </p>

          <div className="mb-5 flex flex-wrap items-end gap-3">
            {/*
             * One-field guarantee (Block 3): exactly ONE of these ever renders.
             *   children === null       → nothing (parent has a loader).
             *   children.length === 0   → age dropdown (today's fallback UX).
             *   children.length === 1   → nothing (auto-selected invisibly).
             *   children.length > 1     → child dropdown.
             */}
            {children && children.length === 0 && (
              <div className="space-y-1.5">
                <Label htmlFor="catalog-cluster" className="text-sm text-dream-navy">
                  {S.clusterLabel}
                </Label>
                <div className="relative">
                  <select
                    id="catalog-cluster"
                    value={ageCluster}
                    onChange={(e) => setAgeCluster(e.currentTarget.value as CatalogAgeCluster)}
                    className="builder-field h-11 w-full appearance-none px-3 pr-10 text-base text-dream-navy outline-none"
                  >
                    <option value="age_3_4">{S.clusterAge3_4}</option>
                    <option value="age_5_7">{S.clusterAge5_7}</option>
                  </select>
                  <ChevronDown
                    aria-hidden
                    className="pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 text-dream-navy"
                  />
                </div>
              </div>
            )}
            {children && children.length > 1 && (
              <div className="space-y-1.5">
                <Label htmlFor="catalog-child" className="text-sm text-dream-navy">
                  {S.childLabel}
                </Label>
                <div className="relative">
                  <select
                    id="catalog-child"
                    value={childId ?? ''}
                    onChange={(e) => setChildId(e.currentTarget.value)}
                    className="builder-field h-11 w-full appearance-none px-3 pr-10 text-base text-dream-navy outline-none"
                  >
                    {children.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    aria-hidden
                    className="pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 text-dream-navy"
                  />
                </div>
              </div>
            )}

            {/* Voice toggle: Männlich / Weiblich (default männlich).
                Header + option labels reused verbatim from the AA/KP/SB
                builder's VoiceSelector via STRINGS.builder.shared.*
                so the catalog picker matches the creation-flow picker
                (emoji Mic in the header, capitalized "Männlich" /
                "Weiblich"). Pill-toggle styling below is unchanged. */}
            <div>
              <h3 className="mb-3 text-sm font-semibold text-dream-navy">
                {STRINGS.builder.shared.voiceSelectorHeader}
              </h3>
              <div className="inline-flex rounded-lg border border-dream-navy/15 p-1">
                <button
                  type="button"
                  onClick={() => setGender('male')}
                  aria-pressed={gender === 'male'}
                  className={`h-9 rounded-md px-4 text-sm font-medium transition ${
                    gender === 'male'
                      ? 'bg-dream-navy text-dream-white'
                      : 'text-dream-navy hover:bg-dream-navy/5'
                  }`}
                >
                  {STRINGS.builder.shared.voiceMale}
                </button>
                <button
                  type="button"
                  onClick={() => setGender('female')}
                  aria-pressed={gender === 'female'}
                  className={`h-9 rounded-md px-4 text-sm font-medium transition ${
                    gender === 'female'
                      ? 'bg-dream-navy text-dream-white'
                      : 'text-dream-navy hover:bg-dream-navy/5'
                  }`}
                >
                  {STRINGS.builder.shared.voiceFemale}
                </button>
              </div>
            </div>
          </div>

          {/*
           * Paket 3 — whole-world unlock + Gesamt-Hörbuch block. Sits above
           * the per-Folge list so both actions (unlock-all + audiobook) are
           * primary. The block's state depends on the CURRENT selectors
           * (effectiveCluster + gender) — a user who switches cluster/gender
           * after triggering one combined asset will see the block reset to
           * the "trigger" state for the new combination (up to four rows per
           * world, keyed by (cluster × gender)).
           *
           * Player-mount reuses the existing CombinedAudioPlayer from the
           * series flow AS-IS. seriesId is fed with the catalogWorldId (an
           * opaque identity guard for the audioStore's alreadyThis check —
           * no consumer routes back to /series/{seriesId}, verified per Q3
           * of the BEFORE report). Semantic overload documented at the
           * `seriesId={detail.worldSlug}`-adjacent line.
           */}
          {detail && detail.folgen.length > 0 && (
            <div className="mb-5 flex flex-col gap-2">
              {allUnlocked ? (
                combined?.status === 'ready' && combined.url && combined.durationMs ? (
                  <CombinedAudioPlayer
                    url={combined.url}
                    downloadUrl={combined.downloadUrl}
                    durationMs={combined.durationMs}
                    title={detail.title}
                    seriesTitle={detail.title}
                    /* Paket 3 semantic overload: audioStore's `seriesId` is
                       used here as an opaque identity guard for the
                       'series-combined' track kind — the catalog world's UUID
                       does not collide with real series UUIDs (both v4). No
                       consumer routes back to /series/{seriesId}. Documented
                       in the commit body; audioStore + CombinedAudioPlayer
                       stay under their standing no-touch rules. */
                    seriesId={combined.worldId}
                    episodeCount={detail.folgen.length}
                  />
                ) : combined?.status === 'pending' || combining ? (
                  <Button
                    type="button"
                    disabled
                    aria-disabled="true"
                    className="h-11 w-full gap-2 bg-dream-navy text-sm font-bold text-dream-white opacity-60"
                  >
                    <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                    {S.combinedTrigger}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={() => void handleTriggerCombined()}
                    className="h-11 w-full gap-2 bg-dream-navy text-sm font-bold text-dream-white hover:bg-dream-navy/90"
                  >
                    <Play aria-hidden className="h-4 w-4" />
                    {S.combinedTrigger}
                  </Button>
                )
              ) : (
                <>
                  {credits > 0 && (
                    <Button
                      type="button"
                      onClick={() => void handleUnlockAll()}
                      disabled={unlockingAll}
                      className="h-11 w-full gap-2 bg-dream-gold text-sm font-bold text-dream-navy hover:bg-dream-gold/90 disabled:opacity-60"
                    >
                      {unlockingAll ? (
                        <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                      ) : null}
                      {S.unlockAll}
                    </Button>
                  )}
                  <p className="text-xs text-dream-navy/70">{S.combinedHint}</p>
                </>
              )}
            </div>
          )}

          {actionError && (
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <p className="text-sm text-[color:var(--color-error)]">{actionError}</p>
              {blockedCta && (
                <button
                  type="button"
                  onClick={() => navigate(blockedCta.target)}
                  className="text-sm font-medium text-dream-navy underline hover:text-dream-navy/80"
                >
                  {blockedCta.label}
                </button>
              )}
            </div>
          )}

          <ul className="flex flex-col gap-2">
            {detail.folgen.map((f) => (
              <li
                key={f.episodeNumber}
                className="flex items-center gap-3 rounded-lg bg-blue-bg p-3 text-dream-navy"
              >
                <span className="w-5 shrink-0 text-sm font-semibold text-dream-navy/60">
                  {f.episodeNumber}.
                </span>
                <span
                  className="min-w-0 flex-1 line-clamp-3 text-sm font-medium"
                  title={f.title ?? f.question}
                >
                  {f.title ?? f.question}
                </span>
                {f.unlocked ? (
                  <span className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onPointerDown={() => prefetchListen(f)}
                      onTouchStart={() => prefetchListen(f)}
                      onClick={() => void handleListen(f)}
                      className="flex items-center gap-1 rounded-md bg-dream-navy px-3 py-1.5 text-xs font-medium text-dream-white"
                    >
                      <Play aria-hidden className="h-3.5 w-3.5" />
                      {S.episodeListen}
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleRead(f)}
                      className="flex items-center gap-1 rounded-md border border-dream-navy/20 px-3 py-1.5 text-xs font-medium text-dream-navy"
                    >
                      <BookOpen aria-hidden className="h-3.5 w-3.5" />
                      {S.episodeRead}
                    </button>
                  </span>
                ) : credits > 0 ? (
                  // Catalog has no sequential lock — EVERY not-unlocked Folge
                  // gets its own gold "Freischalten · 1 Credit" button (mirrors
                  // the series gelb "Erstellen · 1 Credit" visual).
                  <Button
                    type="button"
                    onClick={() => void handleUnlock(f)}
                    className="h-9 shrink-0 bg-dream-gold px-3 text-xs font-bold text-dream-navy hover:bg-dream-gold/90"
                  >
                    {S.unlock}
                  </Button>
                ) : (
                  <span className="shrink-0 text-xs text-dream-navy/60">{S.noCreditsHint}</span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </ContentContainer>
  );
}
